import * as React from 'react'
import { useNavigate } from 'react-router-dom'
import { PixelFactoryContext } from 'pixel-base'
import { AjaxResponse } from 'rxjs/ajax'
import { useLocalStorage } from 'shared/hooks'
import {
  getMenu,
  ILoginResponse,
  ISignupResponse,
  login,
  signup,
} from 'utils/api-routes.util'

import { IAuthContextProps, IMenu, IPixelFactory } from '../../decl'

interface UserInfo {
  token?: string
  is_otp_verified?: boolean
  permissions?: string[]
  [key: string]: any
}

interface TenantInfo {
  tenant_id?: string
  store_id?: string
  [key: string]: any
}

export const AuthContext = React.createContext({} as IAuthContextProps)

interface Props {
  children: React.ReactNode
}

const AuthProvider = React.memo(({ children }: Props) => {
  const pixelFactory = React.useContext<IPixelFactory>(PixelFactoryContext)
  const [userInfo, setUserInfo] = useLocalStorage<UserInfo>('user_info', {})
  const [tenantInfo, setTenantInfo] = useLocalStorage<TenantInfo>(
    'tenant_info',
    {},
  )
  const [info, setInfo] = React.useState({
    user_info: {} as UserInfo,
    tenant_info: {} as TenantInfo,
  })
  const [formError, setFormError] = React.useState({
    form: 'login',
    enable: false,
    field: '',
    error: '',
  })
  const isAuthenticated = () => {
    return userInfo.token ? true : false
  }

  const navigate = useNavigate()

  const dispatchLogin = (data) => {
    pixelFactory?.showPageLoader()
    login(data).subscribe((res: ILoginResponse) => {
      pixelFactory?.hidePageLoader()
      if (res) {
        if (res.user) {
          const userInfo = {
            ...res.user,
            token: res.user.token || res.token, // Ensure token is included
          }
          setInfo({
            user_info: userInfo,
            tenant_info: res.tenant,
          })
          setUserInfo(userInfo)
          setTenantInfo(res.tenant)
          // Dispatch auth change event to trigger route updates
          window.dispatchEvent(new Event('auth-change'))
          if (!res.user.is_otp_verified) {
            navigate('/auth/signup-otp')
          } else {
            getMenu().subscribe((menuResponse: AjaxResponse<IMenu>) => {
              const tenant = res.tenant
              if (res && (!tenant || !tenant.store_id)) {
                navigate('/onboarding')
              } else if (
                res?.user.permissions &&
                !res?.user.permissions.includes('order')
              ) {
                navigate(
                  menuResponse.response.data &&
                    menuResponse.response.data[0] &&
                    menuResponse.response.data[0].route,
                )
              } else {
                navigate('/orders')
              }
            })
          }
          // Dispatch auth change event to trigger route updates
          window.dispatchEvent(new Event('auth-change'))
        } else if (res.error) {
          setFormError({
            ...formError,
            form: 'login',
            enable: true,
            field: res.field ? res.field : '',
            error: res.error,
          })
        }
      }
    })
  }

  const dispatchSignup = (data) => {
    pixelFactory?.showPageLoader()
    setFormError({
      ...formError,
      form: 'signup',
      enable: false,
      field: '',
      error: '',
    })
    signup(data).subscribe((res: AjaxResponse<any>) => {
      pixelFactory?.hidePageLoader()
      const signupResponse = res.response as ISignupResponse
      if (signupResponse.user) {
        setInfo({
          user_info: signupResponse.user,
          tenant_info: signupResponse.business,
        })
        setUserInfo(signupResponse.user)
        setTenantInfo(signupResponse.business)

        // Notify app about auth-related storage changes (token/otp state)
        window.dispatchEvent(new Event('auth-change'))

        navigate('/auth/signup-otp')
      } else if (signupResponse.error) {
        setFormError({
          ...formError,
          form: 'signup',
          enable: true,
          field: signupResponse.field ? signupResponse.field : '',
          error: signupResponse.error,
        })
      }
    })
  }

  const dispatchLogout = () => {
    try {
      // Clear all state
      setInfo({
        user_info: {} as UserInfo,
        tenant_info: {} as TenantInfo,
      })
      setUserInfo({})
      setTenantInfo({})

      // Clear storage
      localStorage.clear()
      sessionStorage.clear()

      // Dispatch auth change event
      window.dispatchEvent(new Event('auth-change'))

      // Navigate to login
      navigate('/auth/login', { replace: true })

      // Force reload to clear any remaining state
      window.location.reload()
    } catch (error) {
      console.error('Error during logout:', error)
      // If navigation fails, force reload to login page
      window.location.href = '/auth/login'
    }
  }

  const value = {
    info,
    formError,
    isAuthenticated,
    dispatchLogin,
    dispatchSignup,
    dispatchLogout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
})

AuthProvider.displayName = 'AuthProvider'
AuthContext.displayName = 'AuthContext'

export default AuthProvider
